import styled from "@emotion/styled";
import {
  GelBoxLayout,
  GelContainerLite,
  GelHeading3,
  GelIcon,
  GelCaption,
  GelParagraph,
  GelLink,
  GelScreenDetectorContext,
} from "@tal-gel/components";
import { getGelTokens } from "@tal-gel/theming";
import Pie from "components/components/Pie";
import TransitionComponent from "components/components/Transition";
import useIntersect from "hooks/useIntersect";
import { useContext } from "react";
import PeopleMale from "@tal-gel/theming-plugin-tal/dist/lite/brand-icons/people-male";

const HomeInfographics = () => {
  const { isLargerThanSmScreen, screen } = useContext(GelScreenDetectorContext);
  const [ref, entry] = useIntersect({ threshold: 1 });

  return (
    <section ref={ref} style={{ height: "100%" }}>
      <GelContainerLite
        style={{
          backgroundColor: getGelTokens().global.themeColorBackgroundInverse,
        }}
      >
        <GelContainerLite fixed>
          <GelHeading3
            inverse
            textAlign="center"
            paddingTop={56}
            marginBottom={getGelTokens().global.sizeBaseX4}
          >
            Shining a light on skin safety practices in Australia
          </GelHeading3>
          <GelParagraph inverse textAlign="center">
            With a continued focus on highlighting the importance of being sun safe, TAL has
            commissioned new research<sup>8 </sup>into the skin health habits of Australians. The
            findings paint a concerning picture and go some way to explaining why the incidence of
            skin cancer in Australia remains so high, with 2 in 3 Australians diagnosed with skin
            cancer by the age of 70 according to the{" "}
            <GelLink
              inverse
              meta="Link to Understanding Skin Cancer by Cancer Council of Australia"
              target="_blank"
              href="https://www.cancer.org.au/assets/pdf/understanding-skin-cancer-booklet#_ga=2.58316970.1437819082.1668402475-1717783682.1668402475"
            >
              Cancer Council of Australia.
            </GelLink>
          </GelParagraph>
          {/* <SpotlightContainer>
            <Stat1
              opacity={entry.isIntersecting ? 1 : 0}
              iconSize={isLargerThanSmScreen(screen) ? 88 : 72}
            />
          </SpotlightContainer> */}

          <InfographicContainer>
            <Stat1 iconSize={isLargerThanSmScreen(screen) ? 88 : 72} />
            <Stat2 inverse value={63} />
            <Stat4 inverse value={30} />
            <Stat3 iconSize={isLargerThanSmScreen(screen) ? 88 : 72} />
          </InfographicContainer>
          <GelCaption inverse condensed textAlign="center" paddingBottom={56}>
            All stats in this image refer to the research completed by Edentify
            <sup>8</sup>.
          </GelCaption>
        </GelContainerLite>
      </GelContainerLite>
    </section>
  );
};

export default HomeInfographics;

const SpotlightContainer = styled.div({
  marginTop: 56,
  paddingTop: 56,
  paddingBottom: 80,
});

const InfographicContainer = styled.div({
  display: "grid",
  marginTop: 40,
  marginBottom: 40,
  paddingTop: 40,
  paddingBottom: 40,
  gridTemplateColumns: "repeat(2, 1fr)",
  gridAutoRows: "1fr",
  gap: "0px 0px",
  gridTemplateAreas: `
          ". ."
          ". ."`,
  justifyContent: "center",
  alignContent: "center",
  justifyItems: "center",
  alignItems: "center",

  "> div": {
    borderBottom: 0,
    width: "100%",
    padding: 40,
    minHeight: 332,

    "&:nth-of-type(-n+2)": {
      borderBottom: `1px solid ${getGelTokens().global.themeColorBorderDefault}`,
    },

    "&:nth-of-type(2n-1)": {
      borderRight: `1px solid ${getGelTokens().global.themeColorBorderDefault}`,
    },
  },

  "@media (max-width: 576px)": {
    display: "flex",
    flexDirection: "column",
    "> div": {
      border: 0,

      "&:nth-of-type(-n+2)": {
        borderBottom: 0,
      },

      "&:nth-of-type(2n-1)": {
        borderRight: 0,
      },
    },
  },
});

export const Stat1 = ({ opacity, backgroundColor, padding, iconSize }) => {
  return (
    <GelBoxLayout
      vertical
      gap="small"
      alignmentIndividual="center"
      style={{
        backgroundColor: backgroundColor,
        padding: padding,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
        }}
      >
        <GelIcon
          name="PeopleMale"
          svgCode={PeopleMale}
          brand
          width={iconSize}
          color={getGelTokens().global.themeColorIconDefault}
          style={{ opacity: opacity, transition: `all 0.3s ease-in-out` }}
        ></GelIcon>
        <GelIcon
          name="PeopleMale"
          svgCode={PeopleMale}
          brand
          width={iconSize}
          color={getGelTokens().global.themeColorIconDefault}
          style={{
            opacity: opacity,
            transitionDelay: 2,
            transitionDuration: 0.3,
            transitionTimingFunction: "ease-in-out",
            transitionProperty: "all",
          }}
        ></GelIcon>
        <GelIcon
          name="PeopleMale"
          brand
          svgCode={PeopleMale}
          width={iconSize}
          color={getGelTokens().global.themeColorTextInverse}
          style={{
            opacity: opacity,
            transitionDelay: 4,
            transitionDuration: 0.3,
            transitionTimingFunction: "ease-in-out",
            transitionProperty: "all",
          }}
        ></GelIcon>
      </div>
      <div style={{ opacity: opacity, transition: `all 0.3s ease-in-out` }}>
        <GelHeading3 as="div" textAlign="center" inverse>
          2 in 3 Australians
        </GelHeading3>
        <GelParagraph textAlign="center" inverse>
          agree that they get more unprotected sun exposure than they realise
        </GelParagraph>
      </div>
    </GelBoxLayout>
  );
};

export const Stat2 = ({ inverse, value, backgroundColor, padding }) => {
  return (
    <GelBoxLayout vertical style={{ backgroundColor: backgroundColor, padding: padding }}>
      <Pie value={value} inverse={inverse} color={getGelTokens().global.themeColorTextInverse} />
      <GelParagraph textAlign="center" inverse={inverse}>
        of Australians have not had a professional skin check in the last year
      </GelParagraph>
    </GelBoxLayout>
  );
};

export const Stat3 = ({ opacity, backgroundColor, padding, iconSize }) => {
  return (
    <GelBoxLayout
      vertical
      gap="small"
      alignmentIndividual="center"
      style={{
        justifySelf: "center",
        backgroundColor: backgroundColor,
        padding: padding,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
        }}
      >
        <GelIcon
          name="PeopleMale"
          brand
          width={iconSize}
          color={getGelTokens().global.themeColorIconDefault}
          style={{ opacity: opacity, transition: `all 0.3s ease-in-out` }}
        ></GelIcon>
        <GelIcon
          name="PeopleMale"
          brand
          width={iconSize}
          color={getGelTokens().global.themeColorTextInverse}
          style={{
            opacity: opacity,
            transitionDelay: 2,
            transitionDuration: 0.3,
            transitionTimingFunction: "ease-in-out",
            transitionProperty: "all",
          }}
        ></GelIcon>
        <GelIcon
          name="PeopleMale"
          brand
          width={iconSize}
          color={getGelTokens().global.themeColorTextInverse}
          style={{
            opacity: opacity,
            transitionDelay: 4,
            transitionDuration: 0.3,
            transitionTimingFunction: "ease-in-out",
            transitionProperty: "all",
          }}
        ></GelIcon>
        <GelIcon
          name="PeopleMale"
          brand
          width={iconSize}
          color={getGelTokens().global.themeColorTextInverse}
          style={{
            opacity: opacity,
            transitionDelay: 6,
            transitionDuration: 0.3,
            transitionTimingFunction: "ease-in-out",
            transitionProperty: "all",
          }}
        ></GelIcon>
      </div>
      <div style={{ opacity: opacity, transition: `all 0.3s ease-in-out` }}>
        <GelHeading3 textAlign="center" inverse>
          Close to 1 in 4 Australians
        </GelHeading3>
        <GelParagraph textAlign="center" inverse>
          never self-check their skin for signs of skin cancer
        </GelParagraph>
      </div>
    </GelBoxLayout>
  );
};

export const Stat4 = ({ inverse, value }) => {
  return (
    <GelBoxLayout vertical>
      <Pie value={value} inverse={inverse} color={getGelTokens().global.themeColorTextInverse} />
      <GelParagraph textAlign="center" inverse={inverse}>
        of Australians have never had a professional skin check
      </GelParagraph>
    </GelBoxLayout>
  );
};
